import { useState, useEffect, useCallback } from 'react';

// ── Dropdown menu positioning ───────────────────────────────
// Dropdown panels are rendered into document.body (a portal) instead of inside
// the field, because any ancestor with `overflow: hidden` — form cards, modal
// bodies, scroll containers — clips an absolutely positioned panel. Position is
// fixed to the anchor's viewport rect and recalculated on scroll/resize.
const MENU_MARGIN = 6;
const MENU_MIN_HEIGHT = 180;

export const getMenuPosition = (anchorEl, minWidth = 0) => {
  const rect = anchorEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom - MENU_MARGIN;
  const spaceAbove = rect.top - MENU_MARGIN;
  const flipUp = spaceBelow < MENU_MIN_HEIGHT && spaceAbove > spaceBelow;
  const maxHeight = Math.max(120, Math.floor(flipUp ? spaceAbove : spaceBelow) - 8);

  const width = Math.max(rect.width, minWidth);
  // Keep the panel inside the viewport when it is wider than its anchor
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));

  return {
    position: 'fixed',
    left: Math.round(left),
    width: Math.round(width),
    maxHeight,
    ...(flipUp
      ? { bottom: Math.round(window.innerHeight - rect.top + MENU_MARGIN) }
      : { top: Math.round(rect.bottom + MENU_MARGIN) }),
  };
};

/**
 * Returns inline styles that pin a portalled dropdown to its anchor element.
 * Null while closed — callers should not render the portal until it is set.
 *
 * @param {boolean} open
 * @param {React.RefObject} anchorRef - element the menu should align to
 * @param {number} minWidth - floor for the menu width (narrow controls)
 */
const useAnchoredMenu = (open, anchorRef, minWidth = 0) => {
  const [style, setStyle] = useState(null);

  const reposition = useCallback(() => {
    if (anchorRef.current) setStyle(getMenuPosition(anchorRef.current, minWidth));
  }, [anchorRef, minWidth]);

  useEffect(() => {
    if (!open) { setStyle(null); return; }
    reposition();
    // `true` = capture phase, so scrolling of any nested container fires this
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  return style;
};

export default useAnchoredMenu;
